import * as THREE from "three";

import { resourcesManifest } from "../Resources/manifest.js";
import { Resources } from "../Resources/Resources.js";
import { RendererController } from "../Renderer/Renderer.js";
import { CameraController } from "../Camera/CameraController.js";
import { World } from "../World/World.js";
import { PostProcess } from "../PostProcess/PostProcess.js";
import { Controls } from "../Controls/Controls.js";
import { UIBridge } from "../UI/UIBridge.js";
import { UIController } from "../UI/UIController.js";
import { StateMachine } from "./StateMachine.js";

import { getRendererProfile } from "../utils/device.js";
import { SCENE_STATES } from "../data/sceneStates.js";

export class Experience {
  constructor({ canvas }) {
    this.canvas = canvas;
    this.profile = getRendererProfile();

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color("#b8dcff");
    this.scene.fog = new THREE.Fog("#e8f4ff", 8, 24);

    this.bridge = new UIBridge();
    this.stateMachine = new StateMachine(SCENE_STATES.BOOT);
    this.ui = new UIController({ bridge: this.bridge });

    this.renderer = new RendererController({ canvas, profile: this.profile });
    this.camera = new CameraController({ profile: this.profile });

    this.postProcess = new PostProcess({
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      enabled: this.profile.usePostProcess
    });

    this.resources = new Resources(resourcesManifest, { isMobile: this.profile.isMobile });
    this.world = null;
    this.controls = null;

    this.animationFrame = null;
    this.previousTime = performance.now();
    this.elapsedTime = 0;

    this.pendingProjectSlug = null;

    this.onTransitionStart = this.onTransitionStart.bind(this);
    this.onStateChange = this.onStateChange.bind(this);
    this.onResize = this.onResize.bind(this);

    this.bindBridgeEvents();
    this.bindStateMachineEvents();
  }

  bindBridgeEvents() {
    this.bridge.on("NAVIGATE", (event) => {
      this.navigateTo(event.detail.state, { fromHistory: Boolean(event.detail.fromHistory) });
    });

    this.bridge.on("OPEN_PROJECT", (event) => {
      this.openProject(event.detail.slug);
    });

    this.bridge.on("CLOSE_PROJECT", () => {
      this.closeProject();
    });

    this.bridge.on("CONTACT_SUBMIT", async (event) => {
      const { formData } = event.detail;
      await this.submitContact(formData);
    });
  }

  bindStateMachineEvents() {
    this.stateMachine.on("transitionstart", this.onTransitionStart);
    this.stateMachine.on("statechange", this.onStateChange);
  }

  async init() {
    this.resources.on("progress", (event) => {
      this.ui.setLoaderProgress(event.detail.value);
    });

    const resourceResult = await this.resources.load();

    this.world = new World({
      scene: this.scene,
      resources: this.resources,
      profile: this.profile
    });

    if (resourceResult.total > 0 && resourceResult.errors === resourceResult.total) {
      this.ui.showLoadError();
    }

    this.controls = new Controls({
      bridge: this.bridge,
      stateMachine: this.stateMachine
    });

    this.applyQualityBadge(resourceResult);
    this.ui.hideLoader();

    const pathIsLegal = window.location.pathname.endsWith("/new/datenschutz");
    const initialState = pathIsLegal ? SCENE_STATES.LEGAL : SCENE_STATES.INTRO;

    this.stateMachine.transition(initialState, { replaceHistory: true });

    window.addEventListener("resize", this.onResize);
    this.startLoop();
  }

  applyQualityBadge(resourceResult) {
    let mode = "KRASS";
    const details = [];

    if (this.profile.isMobile) {
      mode = "LIGHT";
      details.push("mobile");
    }

    if (this.profile.prefersReducedMotion) {
      mode = "REDUCED";
      details.push("reduced-motion");
    }

    if (!this.profile.usePostProcess && !this.profile.isMobile && !this.profile.prefersReducedMotion) {
      mode = "LIGHT";
      details.push("postfx-off");
    }

    if (resourceResult.total > 0 && resourceResult.errors === resourceResult.total) {
      mode = "SAFE";
      details.push("asset-fallback");
    } else if (resourceResult.errors > 0) {
      details.push(`${resourceResult.errors}/${resourceResult.total} asset fallback`);
    }

    this.ui.setQualityMode({
      mode,
      details: details.join(", ")
    });

    console.info("[Experience]", {
      mode,
      profile: this.profile,
      resources: resourceResult
    });
  }

  startLoop() {
    const tick = () => {
      const currentTime = performance.now();
      const delta = (currentTime - this.previousTime) / 1000;
      this.previousTime = currentTime;
      this.elapsedTime += delta;

      this.camera.update();
      if (this.world) {
        this.world.update(this.elapsedTime, delta);
      }

      this.postProcess.renderFrame(this.scene, this.camera.instance);
      this.animationFrame = window.requestAnimationFrame(tick);
    };

    tick();
  }

  async onTransitionStart(event) {
    const { to, payload } = event.detail;

    if (this.world) {
      this.world.transitionTo(to, payload);
    }

    await this.camera.applyShot(to);
    this.stateMachine.commitTransition();
  }

  onStateChange(event) {
    const { state, payload } = event.detail;

    const historyOptions = {
      replace: Boolean(payload?.replaceHistory),
      skip: Boolean(payload?.fromHistory)
    };

    this.ui.renderState(state, payload, historyOptions);

    if (state === SCENE_STATES.PROJECTS && this.pendingProjectSlug) {
      const slug = this.pendingProjectSlug;
      this.pendingProjectSlug = null;
      this.openProject(slug);
    }
  }

  navigateTo(state, payload = {}) {
    if (!state || this.stateMachine.currentState === SCENE_STATES.TRANSITIONING) {
      return;
    }

    const normalized = state === "projectDetail" ? SCENE_STATES.PROJECTS : state;

    if (this.stateMachine.currentState === SCENE_STATES.PROJECT_DETAIL) {
      if ([SCENE_STATES.INTRO, SCENE_STATES.ABOUT, SCENE_STATES.SKILLS].includes(normalized)) {
        this.closeProject();
        window.setTimeout(() => this.navigateTo(normalized, payload), 80);
        return;
      }
    }

    this.stateMachine.transition(normalized, payload);
  }

  openProject(slug) {
    if (!slug || this.stateMachine.currentState === SCENE_STATES.TRANSITIONING) {
      return;
    }

    if (this.stateMachine.currentState !== SCENE_STATES.PROJECTS && this.stateMachine.currentState !== SCENE_STATES.PROJECT_DETAIL) {
      this.pendingProjectSlug = slug;
      this.navigateTo(SCENE_STATES.PROJECTS);
      return;
    }

    if (this.stateMachine.currentState === SCENE_STATES.PROJECT_DETAIL) {
      if (this.world) {
        this.world.transitionTo(SCENE_STATES.PROJECT_DETAIL, { slug });
      }
      this.ui.renderState(SCENE_STATES.PROJECT_DETAIL, { slug }, { skip: true });
      return;
    }

    this.stateMachine.transition(SCENE_STATES.PROJECT_DETAIL, { slug });
  }

  closeProject() {
    if (this.stateMachine.currentState === SCENE_STATES.PROJECT_DETAIL) {
      this.stateMachine.transition(SCENE_STATES.PROJECTS);
    }
  }

  async submitContact(formData) {
    try {
      const response = await fetch("/contact", {
        method: "POST",
        body: formData
      });

      const message = await response.text();
      this.bridge.emit("CONTACT_RESULT", {
        ok: response.ok,
        message
      });
    } catch (_error) {
      this.bridge.emit("CONTACT_RESULT", {
        ok: false,
        message: "Fehler beim Senden. Bitte später erneut versuchen."
      });
    }
  }

  onResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.resize(width, height);
    this.renderer.setSize(width, height);
    this.postProcess.setSize(width, height);
  }

  destroy() {
    window.removeEventListener("resize", this.onResize);

    if (this.controls) {
      this.controls.destroy();
    }

    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (this.world) {
      this.world.dispose();
    }

    this.resources.dispose();
    this.postProcess.dispose();
    this.renderer.dispose();
  }
}
